import { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight, Terminal, Code2, Bot, Volume2, Copy, FileText,
  ChevronRight, Play, Globe, Shield, Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

/* ─── Animation Primitives ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
  hidden: {},
};

function Section({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function FadeUp({ children, index = 0, className = '' }: { children: React.ReactNode; index?: number; className?: string }) {
  return (
    <motion.div variants={fadeUp} custom={index} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Waveform Visual ─── */
function WaveformVisual() {
  const [bars] = useState(() =>
    Array.from({ length: 48 }, (_, i) => ({
      delay: i * 0.06,
      height: 20 + Math.sin(i * 0.4) * 30 + Math.random() * 20,
    }))
  );

  return (
    <div className="flex items-center justify-center gap-[3px] h-16">
      {bars.map((bar, i) => (
        <motion.div
          key={i}
          className="w-[3px] rounded-full bg-gradient-to-t from-blue-500 to-indigo-400"
          initial={{ height: 4, opacity: 0.3 }}
          animate={{
            height: [4, bar.height, 4],
            opacity: [0.3, 0.9, 0.3],
          }}
          transition={{
            duration: 1.8 + Math.random() * 0.6,
            repeat: Infinity,
            delay: bar.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

/* ─── Hero ─── */
function Hero() {
  const { t } = useTranslation('landing');
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div ref={ref} className="relative overflow-hidden bg-[#0B0F1A]" style={{ minHeight: '100svh' }}>
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {/* Radial glow */}
      <motion.div
        style={{ y: bgY }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-blue-600/15 via-indigo-600/10 to-transparent blur-3xl"
      />

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 w-full flex flex-col justify-center" >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-0 lg:min-h-[calc(100svh-64px)] lg:flex lg:items-center">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.04] mb-8">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-medium text-gray-400 tracking-wide">
                    {t('hero.badge')}
                  </span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
              >
                {t('hero.title')}
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400 bg-clip-text text-transparent">
                  {t('hero.titleHighlight')}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-6 text-base sm:text-lg text-gray-400 max-w-md leading-relaxed"
              >
                {t('hero.description')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-10 flex flex-wrap gap-3"
              >
                <a
                  href="/register"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 text-sm"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </a>
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-white/10 hover:border-white/20 text-gray-300 hover:text-white font-medium rounded-lg transition-all duration-200 text-sm"
                >
                  <Play className="w-3.5 h-3.5" />
                  {t('hero.demo')}
                </a>
              </motion.div>

              {/* Trust signals */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-14 flex items-center gap-6 text-xs text-gray-500"
              >
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> {t('hero.trust.dataSecurity')}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> {t('hero.trust.fastResponse')}
                </span>
                <span className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> {t('hero.trust.multilingual')}
                </span>
              </motion.div>
            </div>

            {/* Right: Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-6 sm:p-8">
                {/* Terminal header */}
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-red-400/60" />
                  <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
                  <div className="w-2 h-2 rounded-full bg-green-400/60" />
                  <span className="ml-3 text-[11px] text-gray-500 font-mono">voice-api</span>
                </div>

                {/* Waveform */}
                <WaveformVisual />

                {/* API call mockup */}
                <div className="mt-6 space-y-2 font-mono text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400">→</span>
                    <span className="text-gray-500">POST</span>
                    <span className="text-gray-300">/tts/voice-design</span>
                  </div>
                  <div className="pl-4 text-gray-600">
                    <span className="text-blue-400">"text"</span>: <span className="text-amber-300/80">{t('hero.mockup.text')}</span>
                  </div>
                  <div className="pl-4 text-gray-600">
                    <span className="text-blue-400">"voice"</span>: <span className="text-amber-300/80">{t('hero.mockup.voice')}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-blue-400">←</span>
                    <span className="text-emerald-400">200 OK</span>
                    <span className="text-gray-500">· 0.8s · output.wav</span>
                  </div>
                </div>
              </div>

              {/* Glow effect behind card */}
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/10 to-indigo-600/5 rounded-3xl blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </div>
  );
}

/* ─── Features ─── */
function Features() {
  const { t } = useTranslation('landing');

  const features = [
    {
      icon: Volume2,
      title: t('features.voiceDesign.title'),
      desc: t('features.voiceDesign.desc'),
      endpoint: '/tts/voice-design',
    },
    {
      icon: Copy,
      title: t('features.voiceClone.title'),
      desc: t('features.voiceClone.desc'),
      endpoint: '/tts/controllable-clone',
    },
    {
      icon: FileText,
      title: t('features.transcribe.title'),
      desc: t('features.transcribe.desc'),
      endpoint: '/asr/transcribe',
    },
  ];

  return (
    <Section className="py-28 sm:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <FadeUp>
          <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('features.subtitle')}</p>
        </FadeUp>
        <FadeUp index={1}>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            {t('features.title')}
          </h2>
        </FadeUp>
        <FadeUp index={2}>
          <p className="mt-4 text-gray-500 text-lg max-w-xl">
            {t('features.description')}
          </p>
        </FadeUp>

        <div className="mt-16 divide-y divide-gray-100">
          {features.map((f, i) => (
            <FadeUp key={f.title} index={i}>
              <div className="group py-10 first:pt-0 last:pb-0 grid sm:grid-cols-[auto_1fr_auto] gap-6 sm:gap-10 items-center">
                <div className="flex items-center gap-4 sm:w-48">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{f.title}</h3>
                </div>
                <p className="text-gray-500 text-sm sm:text-base leading-relaxed">{f.desc}</p>
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 border border-gray-100 font-mono text-xs text-gray-400">
                  <span className="text-blue-500">POST</span> {f.endpoint}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── How it works ─── */
function HowItWorks() {
  const { t } = useTranslation('landing');

  const steps = [
    { num: '01', title: t('steps.step1.title'), desc: t('steps.step1.desc') },
    { num: '02', title: t('steps.step2.title'), desc: t('steps.step2.desc') },
    { num: '03', title: t('steps.step3.title'), desc: t('steps.step3.desc') },
  ];

  return (
    <Section className="py-28 sm:py-36 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <FadeUp>
          <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('steps.subtitle')}</p>
        </FadeUp>
        <FadeUp index={1}>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            {t('steps.title')}
          </h2>
        </FadeUp>

        <div className="mt-16 grid md:grid-cols-3 gap-12">
          {steps.map((s, i) => (
            <FadeUp key={s.num} index={i}>
              <div>
                <span className="text-5xl font-bold text-gray-100">{s.num}</span>
                <h3 className="mt-2 text-xl font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-3 text-gray-500 leading-relaxed text-sm">{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Skill Integration ─── */
function SkillSection() {
  const { t } = useTranslation('landing');

  const agents = [
    { icon: Terminal, name: 'Claude Code', install: t('agents.claudeCode.install') },
    { icon: Code2, name: 'VS Code Copilot', install: t('agents.vscodeCopilot.install') },
    { icon: Bot, name: 'OpenAI Codex', install: t('agents.openaiCodex.install') },
  ];

  return (
    <Section className="py-28 sm:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="grid lg:grid-cols-[1fr_auto] gap-16 items-start">
          <div>
            <FadeUp>
              <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('agents.subtitle')}</p>
            </FadeUp>
            <FadeUp index={1}>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                {t('agents.title')}
              </h2>
            </FadeUp>
            <FadeUp index={2}>
              <p className="mt-4 text-gray-500 text-lg max-w-lg leading-relaxed">
                {t('agents.description')}
              </p>
            </FadeUp>
            <FadeUp index={3}>
              <a
                href="https://github.com/shuzibabay-aigc/aivoice-skill"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 text-blue-600 hover:text-blue-500 font-medium text-sm transition-colors"
              >
                {t('agents.viewGithub')}
                <ChevronRight className="w-4 h-4" />
              </a>
            </FadeUp>
          </div>

          <div className="lg:w-[380px] space-y-3">
            {agents.map((a, i) => (
              <FadeUp key={a.name} index={i}>
                <div className="group flex items-start gap-4 p-5 rounded-xl border border-gray-100 hover:border-blue-100 hover:bg-blue-50/30 transition-all duration-300">
                  <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <a.icon className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{a.name}</h3>
                    <p className="mt-1 text-xs text-gray-400 font-mono break-all">{a.install}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── CTA ─── */
function CTA() {
  const { t } = useTranslation('landing');

  return (
    <Section className="py-28 sm:py-36 bg-[#0B0F1A]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 text-center">
        <FadeUp>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            {t('cta.title')}
          </h2>
        </FadeUp>
        <FadeUp index={1}>
          <p className="mt-6 text-gray-400 text-lg max-w-md mx-auto">
            {t('cta.description')}
          </p>
        </FadeUp>
        <FadeUp index={2}>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="/register"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20"
            >
              {t('cta.register')}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/10 hover:border-white/20 text-gray-300 hover:text-white font-medium rounded-lg transition-all duration-200"
            >
              {t('cta.viewPricing')}
            </a>
          </div>
        </FadeUp>
      </div>
    </Section>
  );
}

/* ─── Footer ─── */
function Footer() {
  const { t } = useTranslation('landing');

  return (
    <footer className="py-10 bg-[#080B14] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
        <div className="flex items-center gap-6">
          <a href="/api-docs" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">
            {t('footer.apiDocs')}
          </a>
          <a href="https://github.com/shuzibabay-aigc/aivoice-skill" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">
            {t('footer.github')}
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ─── */
export default function Landing() {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <SkillSection />
      <CTA />
      <Footer />
    </div>
  );
}
